import React, { useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';

import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
} from '../../util/types';
import { formatMoney } from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import { userDisplayNameAsString } from '../../util/data';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  ModalInMobile,
  PrimaryButton,
  AvatarSmall,
  H1,
  H2,
  Icons,
  Button,
  Modal,
} from '../../components';

import css from './OrderPanel.module.css';
import { BOOKING_TYPES, listingPricing } from '../../util/enums';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { useDispatch, useSelector } from 'react-redux';
import BiddingForm from './BiddingForm/BiddingForm';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import {
  getAuctionBuyNowPrice,
  getAuctionExpires,
  getListingBids,
  getListingPricingType,
} from '../../util/dataExtractor';
import moment from 'moment';
import { handleBidTransaction } from '../../util/ListingPageHelpers';
import {
  bidExistenceSelector,
  bidTransactionSelector,
} from '../../containers/ListingPage/ListingPage.duck';
import { currentUserSelector } from '../../ducks/user.duck';
import { formatUnixToCustomDate, isArrayLength, maskUserFullName } from '../../util/genericHelpers';
import ViewBidsModal from './ViewBidsModal';
import { VIEW_BID_LENGTH } from '../../util/constants';

const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(/* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm')
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (isOwnListing, isClosed, history, location) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({ ...parse(search), orderOpen: true })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const renderOfferButton = (
  handleSubmit,
  isOwnListing,
  isClosed,
  showInquiryForm,
  onSubmit,
  history,
  location,
  isOutOfStock
) => {
  const handleClick = handleSubmit(
    isOwnListing,
    isClosed,
    showInquiryForm,
    onSubmit,
    history,
    location,
    BOOKING_TYPES.OFFER
  );

  return (
    <PrimaryButton onClick={handleClick} disabled={isOutOfStock}>
      <FormattedMessage id="OrderPanel.makeAnOffer" />
    </PrimaryButton>
  );
};

const renderPurchaseButton = (
  handleSubmit,
  isOwnListing,
  isClosed,
  showInquiryForm,
  onSubmit,
  history,
  location,
  isOutOfStock,
  isBooking,
  isPurchase
) => {
  const handleClick = handleSubmit(
    isOwnListing,
    isClosed,
    showInquiryForm,
    onSubmit,
    history,
    location,
    BOOKING_TYPES.PURCHASE
  );

  const getMessageId = () => {
    if (isBooking) {
      return 'OrderPanel.ctaButtonMessageBooking';
    }
    if (isOutOfStock) {
      return 'OrderPanel.ctaButtonMessageNoStock';
    }
    if (isPurchase) {
      return 'OrderPanel.ctaButtonMessagePurchase';
    }
    return 'OrderPanel.ctaButtonMessageInquiry';
  };

  return (
    <PrimaryButton onClick={handleClick} disabled={isOutOfStock}>
      <FormattedMessage id={getMessageId()} />
    </PrimaryButton>
  );
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () => openOrderModal(isOwnListing, isClosed, history, location);
};

const dateFormattingOptions = { month: 'short', day: 'numeric', weekday: 'short' };

const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <p className={css.price}>{formatMoney(intl, price)}</p>
      <div className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  );
};

const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    sendInquiryInProgress,
    onSendInquiry,
    isMobileLayout,
  } = props;
  const { BLIND_AUCTION, FIXED_PRICE, ONE_WEEK_AUCTION } = listingPricing;

  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const dispatch = useDispatch();
  const { initiateBidInProgress } = useSelector(bidTransactionSelector);
  const { isBidPlaced } = useSelector(bidExistenceSelector);
  const currentUser = useSelector(currentUserSelector);
  const publicData = listing?.attributes?.publicData || {};
  const { listingType, unitType, transactionProcessAlias = '' } = publicData || {};
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;
  const price = listing?.attributes?.price;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;
  const isBooking = isBookingProcess(processName);
  const listingBids = getListingBids(listing);

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock = isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo.map(info => info.name).includes(processName);

  const { pickupEnabled, shippingEnabled } = listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [STOCK_MULTIPLE_ITEMS].includes(
    listingTypeConfig?.stockType
  );

  const showClosedListingHelpText = listing.id && isClosed;
  const isOrderOpen = !!parse(location.search).orderOpen;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);
  const [isBidModalOpen, setIsBidModalOpen] = useState(false);
  const [isBidViewModalOpen, setIsBidViewModalOpen] = useState(false);
  const auctionExpires = getAuctionExpires(listing);
  const listingPricingType = getListingPricingType(listing);
  const buyNowPrice = getAuctionBuyNowPrice(listing);
  const shouldShowPlaceBidButton =
    !isOwnListing && !isBidPlaced && [ONE_WEEK_AUCTION, BLIND_AUCTION].includes(listingPricingType);
  const shouldShowViewBidsButton = [ONE_WEEK_AUCTION, BLIND_AUCTION].includes(listingPricingType);
  const hasBuyNowPrice = listingPricingType === ONE_WEEK_AUCTION && !!buyNowPrice;
  const shouldRenderProductOrderForm =
    !isOwnListing && (listingPricingType === FIXED_PRICE || hasBuyNowPrice);
  const maskCurrentUserName = maskUserFullName(currentUser);
  const isAuctionListing = [ONE_WEEK_AUCTION, BLIND_AUCTION].includes(listingPricingType);

  const maxPrice = isArrayLength(listingBids)
    ? Math.max(...listingBids.map(bid => bid.bidAmount))
    : price?.amount;

  return (
    <div className={classes}>
      <ModalInMobile
        containerClassName={css.modalContainer}
        id="OrderFormInModal"
        isModalOpenOnMobile={isOrderOpen}
        onClose={() => closeOrderModal(history, location)}
        showAsModalMaxWidth={MODAL_BREAKPOINT}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <div className={css.modalHeading}>
          <H1 className={css.heading}>{title}</H1>
        </div>

        <div className={css.orderHeading}>
          {titleDesktop ? titleDesktop : <H2 className={titleClasses}>{title}</H2>}
          {subTitleText ? <div className={css.orderHelp}>{subTitleText}</div> : null}
        </div>

        {!auctionExpires ? (
          <PriceMaybe
            price={price}
            publicData={publicData}
            validListingTypes={validListingTypes}
            intl={intl}
            marketplaceCurrency={marketplaceCurrency}
          />
        ) : null}

        <div className={css.author}>
          <AvatarSmall user={author} className={css.providerAvatar} />
          <span className={css.providerNameLinked}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorLink }} />
          </span>
          <span className={css.providerNamePlain}>
            <FormattedMessage id="OrderPanel.author" values={{ name: authorDisplayName }} />
          </span>
        </div>

        {auctionExpires && (
          <div className={css.maxPrice}>
            <FormattedMessage id="OrderPanel.currentBid" defaultMessage="Current Bid" />
            <p className={css.maxPriceAmount}>${Number(maxPrice / 100).toFixed(2)}</p>
          </div>
        )}

        {shouldShowViewBidsButton && (
          <div className={css.viewBid}>
            <p className={css.activeBid} onClick={() => setIsBidViewModalOpen(!isBidViewModalOpen)}>
              {listingBids && listingBids?.length > VIEW_BID_LENGTH?.LEN_0 ? (
                <>
                  {listingBids?.length}{' '}
                  <FormattedMessage
                    id={
                      listingBids?.length === VIEW_BID_LENGTH?.LEN_1
                        ? 'OrderPanel.viewBidsCount'
                        : 'OrderPanel.viewBidsCounts'
                    }
                  />
                </>
              ) : (
                <>
                  {VIEW_BID_LENGTH?.LEN_0} <FormattedMessage id="OrderPanel.viewBidsCount" />
                </>
              )}
            </p>
          </div>
        )}

        {shouldRenderProductOrderForm ? (
          <ProductOrderForm
            formId="OrderPanelProductOrderForm"
            onSubmit={onSubmit}
            price={price}
            marketplaceCurrency={marketplaceCurrency}
            currentStock={currentStock}
            allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
            pickupEnabled={pickupEnabled && displayPickup}
            shippingEnabled={shippingEnabled && displayShipping}
            displayDeliveryMethod={false}
            listingId={listing.id}
            isOwnListing={isOwnListing}
            marketplaceName={marketplaceName}
            onFetchTransactionLineItems={onFetchTransactionLineItems}
            onContactUser={onContactUser}
            lineItems={lineItems}
            fetchLineItemsInProgress={fetchLineItemsInProgress}
            fetchLineItemsError={fetchLineItemsError}
            payoutDetailsWarning={payoutDetailsWarning}
            sendInquiryInProgress={sendInquiryInProgress}
            onSendInquiry={onSendInquiry}
            history={history}
            currentUser={currentUser}
            listing={listing}
            isMobileLayout={isMobileLayout}
            hasBuyNowPrice={hasBuyNowPrice}
            listingPricingType={listingPricingType}
            isAuctionListing={isAuctionListing}
          />
        ) : showInquiryForm ? (
          <InquiryWithoutPaymentForm formId="OrderPanelInquiryForm" onSubmit={onSubmit} />
        ) : !isKnownProcess ? (
          <p className={css.errorSidebar}>
            <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
          </p>
        ) : null}
      </ModalInMobile>
      <div className={css.openOrderForm}>
        <PriceMaybe
          price={price}
          publicData={publicData}
          validListingTypes={validListingTypes}
          intl={intl}
          marketplaceCurrency={marketplaceCurrency}
          showCurrencyMismatch
        />
        {shouldShowPlaceBidButton && (
          <div className={css.placeBidButton}>
            <Button
              type="button"
              className={css.active}
              onClick={() => setIsBidModalOpen(!isBidModalOpen)}
            >
              <FormattedMessage id="OrderPanel.placeBid" />
              <Icons name="auction" />
            </Button>
          </div>
        )}
        {isAuctionListing ? (
          <></>
        ) : isClosed ? (
          <div className={css.closedListingButton}>
            <FormattedMessage id="OrderPanel.closedListingButtonText" />
          </div>
        ) : (
          <>
            {renderPurchaseButton(
              handleSubmit,
              isOwnListing,
              isClosed,
              showInquiryForm,
              onSubmit,
              history,
              location,
              isOutOfStock,
              isBooking,
              isPurchase
            )}
            {renderOfferButton(
              handleSubmit,
              isOwnListing,
              isClosed,
              showInquiryForm,
              onSubmit,
              history,
              location,
              isOutOfStock
            )}
          </>
        )}
      </div>
      <div className={css.quantitySec}>
        {shouldShowViewBidsButton && isMobileLayout && (
          <div className={css.viewBid}>
            <p className={css.activeBid} onClick={() => setIsBidViewModalOpen(!isBidViewModalOpen)}>
              {listingBids && listingBids?.length > VIEW_BID_LENGTH?.LEN_0 ? (
                <>
                  {listingBids?.length}{' '}
                  <FormattedMessage
                    id={
                      listingBids?.length === VIEW_BID_LENGTH?.LEN_1
                        ? 'OrderPanel.viewBidsCount'
                        : 'OrderPanel.viewBidsCounts'
                    }
                  />
                </>
              ) : (
                <>
                  {VIEW_BID_LENGTH?.LEN_0} <FormattedMessage id="OrderPanel.viewBidsCount" />
                </>
              )}
            </p>
          </div>
        )}
        {auctionExpires ? (
          <p className={css.auctionExpires}>{formatUnixToCustomDate(auctionExpires)}</p>
        ) : null}
        {/* {shouldShowViewBidsButton && (
          <>
            <H2>
              <FormattedMessage id="OrderPanel.placeBid" />
            </H2>
            <div className={css.placeBidButton}>
              <Button
                type="button"
                className={css.active}
                onClick={() => setIsBidViewModalOpen(!isBidViewModalOpen)}
              >
                <FormattedMessage id="OrderPanel.viewBids" />
                <Icons name="auction" />
              </Button>
            </div>
          </>
        )} */}
        {shouldShowPlaceBidButton && !isMobileLayout && (
          <div className={css.placeBidButton}>
            <Button
              type="button"
              className={css.active}
              onClick={() => setIsBidModalOpen(!isBidModalOpen)}
            >
              <FormattedMessage id="OrderPanel.placeBid" />
              <Icons name="auction" />
            </Button>
          </div>
        )}

        <Modal
          id="SubmitBid.modal"
          isOpen={isBidModalOpen}
          onClose={() => setIsBidModalOpen(false)}
          onManageDisableScrolling={(componentId, disableScrolling) =>
            dispatch(manageDisableScrolling(componentId, disableScrolling))
          }
          modalTitle={intl.formatMessage({
            id: 'OrderPanel.bidModalTitle',
          })}
        >
          <BiddingForm
            onSubmit={values => {
              const { biddingPrice } = values || {};
              handleBidTransaction({
                dispatch,
                bidderId: currentUser?.id?.uuid,
                biddrName: maskCurrentUserName,
                bidAmount: biddingPrice?.amount,
                bidCurrency: biddingPrice?.currency,
                bidPlacedAt: moment().unix(),
                listingId: listing?.id?.uuid,
                processAlias: transactionProcessAlias,
                history,
                routeConfiguration,
                auctionExpires,
              });
            }}
            marketplaceCurrency={marketplaceCurrency}
            updateInProgress={initiateBidInProgress}
            listingMinimumPriceSubUnits={price?.amount}
          />
        </Modal>

        <ViewBidsModal
          listing={listing}
          isOpen={isBidViewModalOpen}
          onClose={() => setIsBidViewModalOpen(false)}
        />
      </div>
    </div>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(OrderPanel);
